import type { MeetingKindList } from "@baton/api";
import { keys, type Runtime } from "@baton/core";
import type { RecentColors } from "@baton/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { projectLabelsQuery, statesQuery } from "../../app/queries.ts";

/**
 * The Recent row of every colour picker in a Team (2026-09-30, § Pickers, ColorPicker): the Team's own
 * colours, read from the values it already holds, newest first. The sources are the colours people
 * choose in a Team: its states and its meeting kinds, which are the Team's, and the labels of the
 * project on screen, labels being a project's. The labels of the Team's other projects are not read,
 * since 001 has no Team-wide label read and reading every project's would be one request per project
 * each time a picker opens; a colour used only on another project's labels is missing from Recent
 * until that project is open. Nothing is read until a picker opens and asks.
 */
export interface DatedColour {
  readonly color: string | null | undefined;
  /** When the value was last written, or null when its source carries no time (a meeting kind). */
  readonly at: string | null;
}

/** The colours newest first, a value with no time after every dated one in its source's order, each colour once. */
export function recentColours(values: readonly DatedColour[]): readonly string[] {
  const dated = values.flatMap((value, index) => (value.color === null || value.color === undefined ? [] : [{ color: value.color.toLowerCase(), at: value.at === null ? null : Date.parse(value.at), index }]));
  dated.sort((a, b) => (a.at === null ? (b.at === null ? a.index - b.index : 1) : b.at === null ? -1 : b.at - a.at));
  return [...new Set(dated.map((value) => value.color))];
}

export function useTeamRecentColours(runtime: Runtime, ws: string, project: string | null, member: boolean): RecentColors {
  const [wanted, setWanted] = useState(false);
  const request = useCallback(() => {
    setWanted(true);
  }, []);
  const states = useQuery({ ...statesQuery(runtime, ws), enabled: wanted && ws.length > 0 });
  // A guest reads no meeting kinds (018 FR-018-268, § Who sees which page).
  // The kinds are read by the meetings area's module, loaded when a picker first asks, so the shell's
  // first load carries none of the meetings reads (FR-015-120, 2026-10-05); the key is the one
  // `meetingKindsQuery` reads under, so the two share one cache entry.
  const kinds = useQuery({
    queryKey: keys.meetingKinds(ws),
    queryFn: async ({ signal }): Promise<MeetingKindList> => (await import("../../meetings/kind/kinds.ts")).readMeetingKinds(runtime, ws, signal),
    enabled: wanted && member,
  });
  const labels = useQuery({ ...projectLabelsQuery(runtime, ws, project ?? ""), enabled: wanted && project !== null });
  const colors = useMemo(
    () =>
      recentColours([
        ...(states.data ?? []).map((state) => ({ color: state.color, at: state.updated_at })),
        ...(labels.data ?? []).map((label) => ({ color: label.color, at: label.updated_at })),
        ...(kinds.data?.items ?? []).map((kind) => ({ color: kind.color, at: null })),
      ]),
    [states.data, labels.data, kinds.data],
  );
  return useMemo(() => ({ colors, request }), [colors, request]);
}
